import React, { FC } from "react";
import { GpmThreshold, Gpm as GpmDataType, isRangeGpm } from "../../../data-types";
import styles from "./gpm.css";
import { GpmRanged } from "./gpm-ranged";
import { AddPricingTooltip } from "../add-pricing-tooltip";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useTaxonomyLabel, VPDLabel } from "~/context/taxonomy-context";

const {
  ["gpm"]: gpmClassName,
  ["danger"]: dangerClassName,
  ["warning"]: warningClassName,
  ["good"]: goodClassName,
  required: requiredClassName,
} = styles as any;

export type GpmProps = {
  gpm: GpmDataType | null;
  gpmThreshold: GpmThreshold | null;
  isFeeEarnerLevelGpm: boolean;
  showRequired?: boolean;
  showLabel?: boolean;
  decimalPlaces?: number;
};

export const Gpm: FC<GpmProps> = ({
  gpm,
  gpmThreshold,
  isFeeEarnerLevelGpm,
  showRequired = true,
  showLabel = false,
  decimalPlaces = 0,
}) => {
  const taxonomy = useTaxonomyLabel();
  const FE_LABEL = taxonomy.FeeEarner;
  const FE_LEVEL_LABEL = taxonomy.FeeEarnerLevel;

  return gpm != null ? (
    <div className={gpmClassName}>
      {isRangeGpm(gpm) ? (
        <>
          {showLabel && <span>GPM:&nbsp;</span>}
          <GpmRanged
            gpm={gpm}
            getGpmClass={(gpm) => getLabelColour(gpm, gpmThreshold)}
            decimalPlaces={decimalPlaces}
          />
          {isFeeEarnerLevelGpm && (
            <Tooltip
              title={`This is an approximate GPM due to this proposal using ${FE_LEVEL_LABEL}s and not specific ${FE_LABEL}s.`}
              placement="topRight"
            >
              <InfoCircleOutlined
                twoToneColor="#eb2f96"
                size={1}
                style={{ color: "orange", fontSize: "14px" }}
              />
            </Tooltip>
          )}
        </>
      ) : (
        <>
          {showLabel && <span>GPM:&nbsp;</span>}
          <span data-test-id="fixed-gpm" className={getLabelColour(gpm.fixed, gpmThreshold)}>
            {Number(gpm.fixed.toFixed(decimalPlaces))}%
          </span>
          {isFeeEarnerLevelGpm && (
            <Tooltip
              title={`This is an approximate GPM due to this proposal using ${FE_LEVEL_LABEL}s and not specific ${FE_LABEL}s.`}
              placement="topRight"
            >
              <InfoCircleOutlined style={{ color: "orange", fontSize: "14px" }} />
            </Tooltip>
          )}
        </>
      )}
      {showRequired && (
        <span className={requiredClassName}>
          <Tooltip
            overlayStyle={{ maxWidth: "500px" }}
            title={`This is a ${gpmThreshold?.source} required GPM.`}
            placement="topLeft"
          >
            <span>Required:</span>
          </Tooltip>
          {Number(gpmThreshold?.gpmDangerThreshold.toFixed(decimalPlaces))}%
        </span>
      )}
    </div>
  ) : (
    <AddPricingTooltip>Not Available</AddPricingTooltip>
  );

  function getLabelColour(value: number, threshold: GpmThreshold | null): string {
    if (threshold === null) {
      return "";
    } else if (threshold.gpmWarningThreshold) {
      // amber percent is a percentage of the gpmTarget. e.g. if target is 70% and warning threshold is 10% then amberPercent is 7%.
      const amberPercent = (threshold.gpmWarningThreshold * threshold.gpmDangerThreshold) / 100;
      const redPercent = threshold.gpmDangerThreshold - amberPercent;
      return value < redPercent
        ? dangerClassName
        : value < redPercent + amberPercent
        ? warningClassName
        : goodClassName;
    } else {
      return value >= threshold.gpmDangerThreshold ? goodClassName : dangerClassName;
    }
  }
};
